import SocialMediaLogos from '@/components/atoms/SocialMediaLogos/SocialMediaLogos';
import FullLoginForm from '@/components/organisms/FullLoginForm/FullLoginForm';
import ErrorMessage from '@/components/atoms/ErrorMessage/ErrorMessage';
import { NextPageLayout } from '@/utils/types/getLayout';
import { InputAdornment } from '@mui/material';
import { Button, Icon, IconButton, TextField, LoadingIcon } from '@toctoc/design-system';
import { GetStaticProps, InferGetStaticPropsType } from 'next';
import Layout from 'pages/_layout';
import { useAppDispatch, useAppSelector } from '@/app/hooks';
import {
	signIn,
	selectLogInAttemptsLeft,
	selectLogInStatus,
	selectStatusCode,
	setResetValues,
	selectTokensData,
	selectURI,
	selectStatus,
	setIsGuest
} from '@/app/reducers/login';
import {
	selectToken,
	setResetToken,
	setToken
} from '@/app/reducers/recaptcha';
import { emailValidator } from '@/utils/emailValidator';
import React, { useCallback, useEffect, useState } from 'react';
import { useRouter } from 'next/router';
import { getErrorMessage } from '@/utils/getErrorMessage';
import { setOriginUrl } from '@/app/reducers';
import Link from 'next/link';
import { STATUS } from '@/app/globalContext/globalContext.interfaces';
import { setCookie } from '@/utils/cookies';

const tryParseUrl = (u:string) => {
	try{
		if(u === undefined || u === null || u === '' || u.trim() === '')
			return undefined;
		new URL(u);
		return u
	} catch(ex) { return undefined; }
}

export const getStaticProps: GetStaticProps<any> = async ({ params }) => {
	const { setupServices } = (await import('../../services.setup.client'));
	const { urlBase, urlTocTocImg, recaptchaSiteKey, loginProvidersData, isRecaptchaEnabled, urlHomeToctoc } = setupServices();

	const loginProviders = await loginProvidersData();

	return {
		props: {
			urlBase,
			urlTocTocImg,
			loginProviders,
			recaptchaSiteKey,
			isRecaptchaEnabled,
			urlHomeToctoc
		}
	}
}

const Login: NextPageLayout<InferGetStaticPropsType<typeof getStaticProps>> = (props: InferGetStaticPropsType<typeof getStaticProps>) => {

	const { urlBase, urlTocTocImg, recaptchaSiteKey, isRecaptchaEnabled, loginProviders, urlHomeToctoc } = props ?? {};

	const COOKIE_NAME = 'redirect_url'; // Nombre de la cookie
	const COOKIE_DURATION_TIME = 30; // Duracion de la cookie en segundos

	// States
	const [emailValue, setEmailValue] = useState('');
	const [passwordValue, setPasswordValue] = useState('');
	const [isValid, setIsValid] = useState(false);
	const [showPassword, setShowPassword] = useState(false);
	const [canContinue, setCanContinue] = useState(false);

	// Redux
	const dispatch = useAppDispatch();
	const logInAttemptsLeft = useAppSelector(selectLogInAttemptsLeft);
	const logInStatus = useAppSelector(selectLogInStatus);
	const statusCode = useAppSelector(selectStatusCode);
	const recaptchaToken = useAppSelector(selectToken);
	const loginTokenData = useAppSelector(selectTokensData);
	const loginRedirectUrl = useAppSelector(selectURI);
	const status = useAppSelector(selectStatus);

	// Obtiene mensaje de error
	const errorMessageList = statusCode !== 0 && statusCode !== 200 && getErrorMessage('signin', statusCode)

	const handleEmailValue = (e: React.ChangeEvent<HTMLInputElement>) => {
		const regEx = /^(?!0)[\w.@-]*$/;
		const validInput = regEx.test(e.target.value);
		if (validInput) {
			setEmailValue(e.target.value.toLowerCase());
			setIsValid((emailValidator(e.target.value.toLowerCase())));
		}
	}

	const handlePasswordValue = (e: React.ChangeEvent<HTMLInputElement>) => {
		setPasswordValue(e.target.value);
	}
	const router = useRouter();
	// Obtencion de uri para redirect luego de login y para el boton volver
	const uri = router.query.url as string ?? '';
	const goBackUrl = uri !== '' && tryParseUrl(uri) !== undefined ? Buffer.from(uri, 'base64').toString('ascii') : urlHomeToctoc;
	
	const isGuest = (router.query.guestAllowed as string)?.toLowerCase() === 'true';
	useEffect(() => {
		dispatch(setIsGuest(isGuest));
	}, [dispatch, isGuest]);


	const logInHandler = () => {
		dispatch(signIn({ email: emailValue, password: passwordValue, uri: uri, logInAttemptsLeft: logInAttemptsLeft, recaptchaToken: recaptchaToken }))
	}

	const goBackButtonHandler = () => {
		if (isValid) {
			removeMailHandler()
		}
		else {
			router.push(goBackUrl)
		}
	}

	const removeMailHandler = () => {
		setCanContinue(false);
		setIsValid(false);
		setEmailValue('');
		setPasswordValue('');
		dispatch(setResetValues());
		dispatch(setResetToken());
	}

	const resetRecaptchaToken = () => {
		dispatch(setResetToken());
	}

	const onSubmitEmail = (e: React.FormEvent<HTMLFormElement>) => {
		e.preventDefault();
		if (isValid) {
			setCanContinue(true);
		}
	}

	const onSubmitLogIn = (e: React.FormEvent<HTMLFormElement>) => {
		e.preventDefault();
		logInHandler();
	}

	const pageArrivalEffect = useCallback(() => {
		if (uri === undefined || uri === null || uri.trim() === "")
			return;
		console.log("Updating pageArrivalEffect")
		dispatch(setOriginUrl(uri));
		setCookie(COOKIE_NAME, uri, COOKIE_DURATION_TIME)
	}, [dispatch, uri]);

	useEffect(pageArrivalEffect, [pageArrivalEffect]);

	useEffect(() => {
		if (isRecaptchaEnabled && canContinue && window.grecaptcha !== null && window.grecaptcha !== undefined) {
			grecaptcha.ready(function () {
				grecaptcha.execute(recaptchaSiteKey, { action: 'login' }).then(function (token) {
					dispatch(setToken({ token }))
				});
			});
		}
	}, [canContinue]);

	useEffect(() => {
		if (logInStatus !== undefined && logInStatus) {
			try {
				const BackUrl = Buffer.from(loginRedirectUrl, "base64").toString('ascii')
				const _ = new URL(BackUrl);
				router.push(BackUrl)
			} catch {
				console.warn("Unable to read incoming url");
				const safeGuardUrl = new URL(goBackUrl);
				safeGuardUrl.searchParams.append("token", loginTokenData?.token)
				router.push(safeGuardUrl)
			}
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [logInStatus]);

	useEffect(() => {
		if (isRecaptchaEnabled && statusCode !== 0 && statusCode === 400 && logInAttemptsLeft > 0) {
			grecaptcha.ready(function () {
				grecaptcha.execute(recaptchaSiteKey, { action: 'login' }).then(function (token) {
					dispatch(setToken({ token }))
				});
			});
		}
	}, [statusCode, logInAttemptsLeft]);

	useEffect(() => {
		dispatch(setResetValues());
		dispatch(setResetToken());
		dispatch(setIsGuest(isGuest));
	}, [dispatch]);


	return (
		<FullLoginForm imgSrc={`${urlTocTocImg}/toctocsso/img/person_holding_houses.svg`} uri={goBackUrl}>
			<div className='loginFormCtr'>
				<section className='titleCtr'>
					<h6>¡Bienvenido a TOCTOC!</h6>
					<p>Inicia sesión o crea una cuenta fácilmente.</p>
				</section>
				{loginProviders && loginProviders.length > 0 && <SocialMediaLogos loginProvidersData={loginProviders} urlTocTocImg={urlTocTocImg} />}
				<section className='textCtr'>
					<span>Ingresar con tu email.</span>
					<span>¿No tienes una cuenta? <Link href={isGuest ? `/signup?bypassGuest=true` : `/signup`} >¡Crea una ahora!</Link></span>
				</section>
				<section className='inputsCtr'>
					<div className='emailTextCtr'>
						<form onSubmit={onSubmitEmail}>
							<TextField
								fullWidth id='text-area'
								placeholder='Ingresa tu email'
								label='Email'
								disabled={canContinue ? true : false}
								name='email' size='small'
								value={emailValue}
								onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleEmailValue(e)}
								style={{ backgroundColor: 'transparent' }}
								sx={{
									'& .MuiOutlinedInput-input': {
										color: 'black',
										'&:-webkit-autofill': {
											WebkitBoxShadow: '0 0 0 100px white inset',
											WebkitTextFillColor: 'default',
										},
									},
								}}
								InputProps={{
									endAdornment: (
										canContinue && <InputAdornment position='end'>
											<IconButton onClick={() => removeMailHandler()} style={{ paddingRight: '12px' }} >
												<Icon className='icono' name='close' size={16} color={'#343A40'} />
											</IconButton>
										</InputAdornment>
									)
								}}
							/>
						</form>
						{emailValue && !isValid && <span className='notValidEmail'>Debes ingresar un mail válido.</span>}
					</div>
					{
						canContinue && <>
							<div className='passwordCtr'>
								<form onSubmit={onSubmitLogIn}>
									<TextField
										type={showPassword ? 'text' : 'password'}
										disabled={logInAttemptsLeft === 0}
										fullWidth
										id='text-area'
										placeholder='Ingresa tu contraseña'
										label='Contraseña'
										name='contraseña'
										size='small'
										value={passwordValue}
										onChange={(e: React.ChangeEvent<HTMLInputElement>) => handlePasswordValue(e)}
										InputProps={{
											endAdornment: (
												<InputAdornment position='end'>
													<IconButton disabled={!passwordValue || logInAttemptsLeft === 0} onClick={() => setShowPassword(!showPassword)} >
														{!showPassword ? <Icon className='icono' name='Eye' size={24} color={passwordValue ? '#343A40' : '#B1B1B1'} /> : <Icon className='icono' name='EyeSlash' size={24} color={passwordValue ? '#343A40' : '#B1B1B1'} />}
													</IconButton>
												</InputAdornment>
											)
										}}
									/>
								</form>
								{logInStatus !== undefined && !logInStatus && logInAttemptsLeft > 0 && <span className={`triesLeftText has${logInAttemptsLeft}triesLeft`}>{logInAttemptsLeft > 1 ? `*Te quedan ${logInAttemptsLeft} intentos para ingresar.` : `*Te queda ${logInAttemptsLeft} intento para ingresar.`}</span>}
								<Link href="/recovery" onClick={resetRecaptchaToken}>¿No recuerdas tu contraseña?</Link>
							</div>
							{/* <div className='rememberMeCtr'>
								<span>Te recordaremos</span>
								<Switch disabled={logInAttemptsLeft === 0} />
							</div> */}
						</>
					}
					{errorMessageList && <ErrorMessage errorData={errorMessageList.errorList} />}
					<div className='buttonCtr'>
						{!canContinue && <Button onClick={() => setCanContinue(true)} style={{ height: '40px', fontSize: '14px' }} disabled={isValid ? false : true} texto='Continuar' variant='contained' disableRipple disableFocusRipple fullWidth />}
						{canContinue && <Button onClick={logInHandler} style={{ height: '40px', fontSize: '14px' }} disabled={passwordValue && logInAttemptsLeft > 0 && status !== STATUS.pending ? false : true} variant='contained' disableRipple disableFocusRipple fullWidth >
							{status === STATUS.pending
								? <>
									<LoadingIcon size={16} color='#FFF' marginRight={5} />
									Cargando
								</>
								: "Iniciar sesión"}
						</Button>}
					</div>
					{isGuest &&
						<>
							<div className='underlineCtr'>
								<div>
									<span>También puedes</span>
								</div>
							</div>
							<div className='loginGuessButtonCtr'>
								<Link href="/signup" style={{}}>Continuar como invitado</Link>

							</div>
						</>
					}
					<div className='goBackButtonCtr'>
						<Button onClick={goBackButtonHandler} texto='Volver' variant='text' disableRipple disableFocusRipple style={{ backgroundColor: 'transparent', fontWeight: 700 }} startIcon={<Icon className='btn-icono' name='chevron-left' size={24} color={'#3200c1'} />} />
					</div>
				</section>
			</div>
		</FullLoginForm>
	)
}

Login.getLayout = function getLayout(page, layoutData) {
	return <Layout {...layoutData}>{page}</Layout>;
};
export default Login